import React, { useState } from 'react';
import { WhatsAppIcon, CloseIcon } from '../Icons';
import {
  generateWhatsAppMessage,
  splitBulkWhatsAppMessages as splitBulkWhatsAppMessagesShared,
  openWhatsAppShare,
} from '../../utils/whatsappMessage';
import { derwazaService } from '../../services/api';

const WhatsAppHandler = ({
  agents = [],
  selectedTag,
  selectedTags = [],
  lands = [],
  t,
  language,
  isMobile,
  showSavedOnly = false,
  savedLandIds = [],
  isStaff = false
}) => {
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  // Set when the agent dialog was opened for one specific land (e.g. from
  // DetailsPanel's "Contact Agent" button) — picking an agent then sends
  // just that land's message instead of falling back to the bulk
  // tag/saved-selection behavior below.
  const [whatsappDialogLand, setWhatsappDialogLand] = useState(null);
  // Non-null while the "pick a chunk to send" popup is open. `phone` is null
  // for the generic share button (wa.me contact picker) or a specific
  // agent's number when sent from the agent dialog.
  const [splitMessages, setSplitMessages] = useState(null);
  const [splitPhone, setSplitPhone] = useState(null);

  // Staff-only step in front of a customer-facing share (single land or
  // bulk selection): captures the client's phone number, embeds it
  // directly in the wa.me link instead of falling back to WhatsApp's own
  // contact picker, and logs the send as an operation on the sister site
  // derwazaq8.com — mirrors derwaza's own admin items-table phone modal
  // exactly (see its views/partials/items-table-whatsapp-modal.ejs), so a
  // share from either app shows up the same way in derwaza's "العمليات"
  // report. Never gates a non-staff share — the public "share this
  // listing with a friend" button keeps going straight to WhatsApp.
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [phoneModalValue, setPhoneModalValue] = useState('');
  const [loggingPhoneShare, setLoggingPhoneShare] = useState(false);
  // { lands: Land[], mode: 'single' | 'bulk' } — set right before opening
  // the phone modal, consumed once a phone is submitted or the modal is
  // skipped.
  const [pendingPhoneShare, setPendingPhoneShare] = useState(null);

  const shareOnWhatsApp = (land) => {
    if (isStaff) {
      setPendingPhoneShare({ lands: [land], mode: 'single' });
      setPhoneModalValue('');
      setPhoneModalOpen(true);
      return;
    }
    openWhatsAppShare(generateWhatsAppMessage(land, { language, isStaff }));
  };

  const getSelectedLands = () => {
    if (showSavedOnly) return lands.filter(land => savedLandIds.includes(land.id));
    if (selectedTag) return lands.filter(land => land.tags && land.tags.some(tag => tag.id === selectedTag.id));
    if (selectedTags.length > 0) {
      return lands.filter(land => {
        if (!land.tags) return false;
        const landTagTitles = land.tags.map(tag => tag.title);
        return selectedTags.every(selectedTagTitle => landTagTitles.includes(selectedTagTitle));
      });
    }
    return [];
  };

  const shareBulkOnWhatsApp = () => {
    if (!showSavedOnly && !selectedTag && selectedTags.length === 0) {
      alert(t('الرجاء اختيار تصنيف أولاً أو تفعيل المحفوظة', 'Please select a category first or enable saved lands'));
      return;
    }

    const selectedLands = getSelectedLands();
    if (isStaff) {
      setPendingPhoneShare({ lands: selectedLands, mode: 'bulk' });
      setPhoneModalValue('');
      setPhoneModalOpen(true);
      return;
    }

    setSplitPhone(null);
    setSplitMessages(splitBulkWhatsAppMessagesShared(selectedLands, { language, isStaff }));
  };

  const closePhoneModal = () => {
    setPhoneModalOpen(false);
    setPendingPhoneShare(null);
  };

  // Opens WhatsApp for whatever's pending, targeting `phone` when given
  // (blank falls back to WhatsApp's own contact picker, same as an
  // unstaffed share) — shared by both the logged submit path and the
  // "send without registering" skip.
  const proceedPendingPhoneShare = (phone) => {
    setPhoneModalOpen(false);
    if (!pendingPhoneShare) return;
    const { lands: shareLands, mode } = pendingPhoneShare;
    if (mode === 'single') {
      openWhatsAppShare(generateWhatsAppMessage(shareLands[0], { language, isStaff }), phone || undefined);
    } else {
      setSplitPhone(phone || null);
      setSplitMessages(splitBulkWhatsAppMessagesShared(shareLands, { language, isStaff }));
    }
    setPendingPhoneShare(null);
  };

  const skipPhoneModalLog = () => {
    proceedPendingPhoneShare(phoneModalValue.trim());
  };

  const submitPhoneModal = async () => {
    const phone = phoneModalValue.trim();
    if (!phone) {
      alert(t('الرجاء إدخال رقم الهاتف', 'Please enter a phone number'));
      return;
    }
    setLoggingPhoneShare(true);
    try {
      await derwazaService.logWhatsAppShare(phone, (pendingPhoneShare?.lands || []).map((land) => land.id));
    } catch (error) {
      // Best-effort: the staff member's real task is messaging the client -
      // a failed derwaza log must never block that (see
      // backend/services/derwazaService.js's own no-throw policy).
      console.error('Failed to log WhatsApp share to derwaza:', error);
    } finally {
      setLoggingPhoneShare(false);
    }
    proceedPendingPhoneShare(phone);
  };

  const openWhatsAppDialog = (land = null) => {
    setWhatsappDialogLand(land);
    setWhatsappDialogOpen(true);
  };

  const closeWhatsAppDialog = () => {
    setWhatsappDialogOpen(false);
    setWhatsappDialogLand(null);
  };

  const closeSplitMessages = () => {
    setSplitMessages(null);
    setSplitPhone(null);
  };

  const sendSplitChunk = (chunk) => {
    openWhatsAppShare(chunk.message, splitPhone);
  };

  const openWhatsAppChat = (agent) => {
    const phone = agent.phone || agent.phone_number;

    if (whatsappDialogLand) {
      openWhatsAppShare(generateWhatsAppMessage(whatsappDialogLand, { language, isStaff }), phone);
      closeWhatsAppDialog();
      return;
    }

    const selectedLands = getSelectedLands();

    if (selectedLands.length === 0) {
      const defaultMessage = t(
        'مرحباً، أود الاستفسار عن العقارات المتاحة',
        'Hello, I would like to inquire about available properties'
      );
      openWhatsAppShare(defaultMessage, phone);
      setWhatsappDialogOpen(false);
      return;
    }

    setWhatsappDialogOpen(false);
    setSplitPhone(phone);
    setSplitMessages(splitBulkWhatsAppMessagesShared(selectedLands, { language, isStaff }));
  };

  return {
    shareOnWhatsApp,
    shareBulkOnWhatsApp,
    openWhatsAppDialog,
    
    WhatsAppDialog: () => (
      whatsappDialogOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden min-w-[320px] max-w-md">
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-4 flex justify-between items-center">
              <h2 className="text-lg font-bold">
                {t('تواصل معنا عبر الواتساب', 'Contact Us via WhatsApp')}
              </h2>
              <button
                onClick={closeWhatsAppDialog}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 bg-gradient-to-br from-yellow-50 to-gray-50 max-h-[60vh] overflow-y-auto">
              {(() => {
                const filteredAgents = agents.filter(agent => agent.agent === true);
                console.log('Rendering dialog, filtered agents:', filteredAgents);
                
                if (filteredAgents.length === 0) {
                  return (
                    <p className="text-center py-8 text-gray-600">
                      {t('لا يوجد وكلاء متاحون حالياً', 'No agents available at the moment')}
                    </p>
                  );
                }
                
                return (
                  <div className="space-y-3">
                    {filteredAgents.map((agent) => (
                    <button
                      key={agent.id}
                      onClick={() => openWhatsAppChat(agent)}
                      className="w-full flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-green-500 hover:shadow-lg transition-all duration-200 group"
                    >
                      <div className="flex-1 text-right">
                        <h3 className="font-bold text-gray-900 text-base">
                          {agent.name}
                        </h3>
                        <p className="text-sm text-gray-600" dir="ltr">
                          {agent.phone || agent.phone_number}
                        </p>
                      </div>
                      <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <WhatsAppIcon className="w-7 h-7 text-white" />
                      </div>
                    </button>
                  ))}
                </div>
                );
              })()}
            </div>
          </div>
        </div>
      )
    ),

    SplitMessagesDialog: () => (
      splitMessages && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden min-w-[320px] max-w-md">
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-4 flex justify-between items-center">
              <h2 className="text-lg font-bold">
                {t('اختر رسالة للإرسال', 'Choose a Message to Send')}
              </h2>
              <button
                onClick={closeSplitMessages}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 bg-gradient-to-br from-yellow-50 to-gray-50 max-h-[60vh] overflow-y-auto space-y-3">
              {splitMessages.length > 1 && (
                <p className="text-sm text-gray-600 text-center mb-2">
                  {t(
                    `الرسالة تجاوزت الحد المسموح فتم تقسيمها إلى ${splitMessages.length} رسائل`,
                    `Message exceeded the length limit and was split into ${splitMessages.length} messages`
                  )}
                </p>
              )}
              {splitMessages.map((chunk, index) => (
                <button
                  key={index}
                  onClick={() => sendSplitChunk(chunk)}
                  className="w-full flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-green-500 hover:shadow-lg transition-all duration-200 group"
                >
                  <div className="flex-1 text-right">
                    <h3 className="font-bold text-gray-900 text-base">
                      {t(`رسالة ${index + 1}`, `Message ${index + 1}`)}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t(`${chunk.count} عقار`, `${chunk.count} item${chunk.count === 1 ? '' : 's'}`)}
                    </p>
                  </div>
                  <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <WhatsAppIcon className="w-7 h-7 text-white" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )
    ),

    WhatsAppPhoneModal: () => (
      phoneModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden min-w-[320px] max-w-md">
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-4 flex justify-between items-center">
              <h2 className="text-lg font-bold">{t('إرسال عبر واتساب', 'Send via WhatsApp')}</h2>
              <button onClick={closePhoneModal} className="p-1 hover:bg-white/10 rounded-lg transition-colors">
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 bg-gradient-to-br from-yellow-50 to-gray-50">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {t('رقم هاتف العميل', "Client's phone number")}
              </label>
              <input
                type="tel"
                autoFocus
                value={phoneModalValue}
                onChange={(e) => setPhoneModalValue(e.target.value)}
                placeholder={t('مثال: 99887766', 'e.g. 99887766')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                dir="ltr"
              />
              <p className="text-xs text-gray-500 mt-2">
                {t(
                  'إذا كان الرقم لعميل محفوظ في derwaza سيتم ربط العقارات المرسلة بملفه، وإلا سيتم إنشاء عميل جديد تلقائياً',
                  'If this number matches a saved derwaza client the sent items are linked to their file; otherwise a new client is created automatically'
                )}
              </p>
            </div>

            <div className="p-4 pt-0 bg-gradient-to-br from-yellow-50 to-gray-50 flex gap-3">
              <button
                onClick={skipPhoneModalLog}
                disabled={loggingPhoneShare}
                className="flex-1 px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {t('إرسال بدون تسجيل', 'Send without logging')}
              </button>
              <button
                onClick={submitPhoneModal}
                disabled={loggingPhoneShare}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-green-500 hover:bg-green-600 rounded-lg transition-colors disabled:opacity-50"
              >
                <WhatsAppIcon className="w-4 h-4" />
                {loggingPhoneShare ? t('جارِ الإرسال...', 'Sending...') : t('إرسال', 'Send')}
              </button>
            </div>
          </div>
        </div>
      )
    ),

    WhatsAppButtons: () => (
      <div className="flex flex-col gap-3">
        {/* Agent WhatsApp Contact Button */}
        <button
          onClick={openWhatsAppDialog}
          title={t('تواصل مع الوكلاء', 'Contact Agents')}
          className={`${
            isMobile ? 'w-12 h-12' : 'w-14 h-14'
          } rounded-full bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center`}
        >
          <WhatsAppIcon className={isMobile ? 'w-5 h-5' : 'w-6 h-6'} />
        </button>

        {/* Bulk Share WhatsApp Button - Different color and icon */}
        <button
          onClick={shareBulkOnWhatsApp}
          disabled={!showSavedOnly && !selectedTag && selectedTags.length === 0}
          title={t('مشاركة المحددة عبر الواتساب', 'Share Selected via WhatsApp')}
          className={`${
            isMobile ? 'w-12 h-12' : 'w-14 h-14'
          } rounded-full shadow-lg transition-all flex items-center justify-center ${
            showSavedOnly || selectedTag || selectedTags.length > 0
              ? 'bg-blue-500 hover:bg-blue-600 text-white hover:scale-105 cursor-pointer'
              : 'bg-blue-200 text-blue-400 cursor-not-allowed opacity-50'
          }`}
        >
          {/* Share Icon instead of WhatsApp */}
          <svg className={isMobile ? 'w-5 h-5' : 'w-6 h-6'} fill="currentColor" viewBox="0 0 24 24">
            <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
          </svg>
        </button>
      </div>
    )
  };
};

export default WhatsAppHandler;
